import { BuildTableData } from '../types';
import { ExternalLink } from '@lifeomic/chromicons';
import { IconButtonLink } from '@lifeomic/chroma-react/components/IconButtonLink';
import { Link } from '@lifeomic/chroma-react/components/Link';
import { sharedMessages } from '../../sharedIntlLabels';
import { SpecificityIndicator } from '../../Indicators';
import { TableConfiguration } from '@lifeomic/chroma-react/components/TableModule';
import React from 'react';
import urls from '../../../../../src/util/urls';
import { getReadSetFileName } from '../../useFetchReadSetFiles';

// #region Value getters

export const getCKB = (value: any) => {
  const { drugAssociations } = value;

  if (!drugAssociations) {
    return null;
  }

  const { byGeneAndFeature = [] } = drugAssociations;

  return (
    <SpecificityIndicator
      match={byGeneAndFeature.length > 0 ? 'variant' : 'gene'}
    />
  );
};

export const getGene = (value: any) => {
  const gene = value.gene;

  if (!gene) {
    return null;
  }

  return (
    <Link to={urls.ncbi.gene(gene)} onClick={e => e.stopPropagation()} newTab>
      {gene}
    </Link>
  );
};

export const getGeneTextOnly = (value: any) => value?.gene;

export const getExpression = (value: any) =>
  `${value.expression} ${(value.expressionUnit || '').toUpperCase()}`;

export const getAvg = (value: any) => value?.avg;
export const getStddev = (value: any) => value?.stddev;
export const getMin = (value: any) => value?.min;
export const getQ1 = (value: any) => value?.firstQuartile;
export const getMedian = (value: any) => value?.median;
export const getQ3 = (value: any) => value?.thirdQuartile;
export const getMax = (value: any) => value?.max;

export const getIgv = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  activeSequence,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  account,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  classes,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  formatMessage,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  value,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  variantSet,
}) => {
  const { gene } = value;

  if (!gene) {
    return null;
  }

  const readSets =
    variantSet?.sequenceType &&
    activeSequence.sets.filter(
      (s: any) =>
        s.setType === 'read' && s.sequenceType === variantSet.sequenceType,
    );
  const url = urls.app.genomeViewer({
    account,
    location: gene,
    genome: activeSequence?.referenceSetId,
    readSets: readSets?.flatMap((r: any) => [
      r.id,
      `${r.name} - ${getReadSetFileName(r)}`,
    ]),
  });

  return (
    <IconButtonLink
      aria-label={formatMessage(sharedMessages.viewIgv)}
      className={classes?.innerIconButton}
      paddingTop={0}
      paddingBottom={0}
      paddingLeft={0}
      paddingRight={0}
      icon={ExternalLink}
      size={0}
      to={url}
      target="_blank"
      rel="noopener noreferrer"
      // Stop propagation so the table-row click handler
      // doesn't trigger
      onClick={e => e.stopPropagation()}
    />
  );
};

// #endregion

export const buildTableData = ({
  account,
  activeSequence,
  classes,
  formatMessage,
  onSort,
  variantSet,
  fetchPrependedColumns,
}: BuildTableData): Array<TableConfiguration> => [
  ...(fetchPrependedColumns?.(variantSet, activeSequence) ?? []),
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.ckb),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['drugAssociations.byGene'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getCKB(value),
    },
    isSticky: true,
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.gene),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['gene'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getGene(value),
    },
    isSticky: true,
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.expression),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['expression'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getExpression(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.avg),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['avg'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getAvg(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.stddev),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['stddev'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getStddev(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.min),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['min'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getMin(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.q1),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['firstQuartile'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getQ1(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.median),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['median'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getMedian(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.q3),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['thirdQuartile'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getQ3(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.max),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['max'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getMax(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.igv),
    },
    cell: {
      content: value =>
        getIgv({
          activeSequence,
          account,
          classes,
          formatMessage,
          value,
          variantSet,
        }),
    },
  },
];
